import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import { CONTACT } from "../data/content";
import Seo from "../components/Seo";

const Section = ({ title, children }) => (
    <Reveal>
        <div className="bg-white border border-[#E7E4DC] shadow-sm rounded-xl p-8" data-testid={`terms-section`}>
            <h2 className="font-display text-xl sm:text-2xl text-[#20242B]">{title}</h2>
            <div className="text-[#4A4F58] text-sm sm:text-base leading-relaxed mt-4 space-y-3">{children}</div>
        </div>
    </Reveal>
);

export default function Terms() {
    return (
        <div data-testid="page-terms">
            <Seo
                title="Terms of Service | Freedom Tax Filer"
                description="The terms that govern use of the Freedom Tax Filer website and the tax preparation services provided under your written agreement."
                path="/terms"
            />
            <section className="relative pt-44 pb-14 bg-[#111318] overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(200,164,93,0.10),transparent_60%)]" />
                <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow-dark">Legal</p>
                        <div className="gold-rule mt-5" />
                    </Reveal>
                    <h1 className="font-display text-white text-5xl sm:text-6xl leading-[1.05] tracking-tight mt-8">
                        <MaskedLines delay={0.2} lines={["Terms of", <>Service.</>]} />
                    </h1>
                    <Reveal delay={0.5}>
                        <p className="text-white/60 text-sm mt-8">Last updated: October 2026</p>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad" data-testid="terms-content">
                <div className="mx-auto max-w-3xl px-5 sm:px-8 space-y-5">
                    <Section title="General information only">
                        <p>
                            This website is for general information and inquiries about Freedom Tax Filer's services.
                            Nothing on these pages is tax, legal, or financial advice for your specific situation.
                        </p>
                    </Section>
                    <Section title="Services are governed by a written agreement">
                        <p>
                            Tax preparation and assistance services are provided under the terms of the written service
                            agreement you review before purchasing. That agreement — not this website — defines the scope,
                            eligibility, exclusions, response expectations, and fees for any service, including post-filing
                            notice and audit assistance.
                        </p>
                    </Section>
                    <Section title="No outcome guarantees">
                        <p>
                            We prepare returns carefully and consider every benefit that applies to your circumstances,
                            but all tax benefits depend on your individual situation and applicable law. We do not
                            guarantee a maximum refund, a specific refund amount, a reduction in tax liability, or
                            eligibility for any deduction or credit.
                        </p>
                    </Section>
                    <Section title="Website content">
                        <p>
                            The content, design, and branding of this website belong to Freedom Tax Filer and may not be
                            reproduced without permission.
                        </p>
                    </Section>
                    <Section title="Questions?">
                        <p>
                            Reach us at{" "}
                            <a href={CONTACT.emailHref} className="text-[#8A6D2F] border-b border-[#C8A45D]/50 hover:border-[#C8A45D] transition-colors duration-200" data-testid="terms-email-link">
                                {CONTACT.email}
                            </a>.
                        </p>
                    </Section>
                    <Reveal>
                        <p className="text-[#737985] text-xs leading-relaxed">
                            These terms are a summary and may be updated from time to time.
                        </p>
                    </Reveal>
                    <Reveal>
                        <Link
                            to="/contact"
                            data-testid="terms-back-contact"
                            className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.14em] text-[#8A6D2F] border-b border-[#C8A45D]/50 pb-1 mt-4 hover:border-[#C8A45D] transition-colors duration-200"
                        >
                            Back to Contact <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
